import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'

import { onLeadMagnetFunnelFormDispatch as dispatchSelector } from 'reduxModules/leadMagnetPages/dispatchers'
import { leadMagnetPageStateSelector as stateSelector } from 'reduxModules/leadMagnetPages/selectors'
import EMAIL_PATTERN from '../../../utils/emailPattern'
import InlineError, { ariaErrorProps } from '../../../generic/InlineError'

import '../../../generic/RenderedField/styles.css'
import './styles.css'

// SPEC-091 FR-7: Nate's wording, verbatim. Changing the consent sentence
// means bumping WWW_CONSENT_TEXT_VERSION and backend's text map with it.
export const INTRO_COPY =
  "Free lessons from 16 of our most popular courses, taught by Hollywood's leading FX artists. Plus our newsletter with new courses, artist stories and special offers."
export const CONSENT_LABEL =
  'Yes, send me the free lessons and Stan Winston School emails, including news and offers. Unsubscribe anytime.'
export const CONSENT_WARNING =
  'Tick the box above to get the free lessons and our emails.'
export const PRIVACY_POLICY_URL =
  'https://www.stanwinstonschool.com/customer-care/privacy-policy/'

export function LeadMagnetFunnelForm({
  hasSubscription,
  isPopUp = false,
  tag,
  formValues = { isCTADisabled: false },
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields }
  } = useForm({ defaultValues: { email: '', consent: false } })

  if (hasSubscription) return null

  // SPEC-091: unticked on every mount; the warning waits until the visitor
  // has typed an email, so an untouched form isn't greeted with an error
  const consent = watch('consent')
  const consentId = `${tag}-consent-checkbox`
  const warningId = `${tag}-consent-warning`
  const showWarning = !consent && Boolean(dirtyFields.email)

  return (
    <section id="LeadMagnetFunnelForm" className={isPopUp ? 'popup' : ''}>
      <div className="internal-container wrapper">
        <div className="copy">
          <h2 className="title">
            Get FREE Popular Course Content!
          </h2>
          <div className="subtitle">
            <p className="tagline">{INTRO_COPY}</p>
          </div>
        </div>
        <div className="form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={classnames('field', '-floating', { '-error': errors.email })}>
              <div className="field-container">
                <input
                  {...register('email', {
                    required: 'This field is required.',
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: 'Invalid email address.'
                    }
                  })}
                  type="email"
                  placeholder=" "
                  id={`${tag}-email-field`}
                  key={`${tag}-email-field`}
                  {...ariaErrorProps(`${tag}-email-field`, errors.email)}
                />
                <label htmlFor={`${tag}-email-field`}>Email</label>
                <InlineError
                  id={`${tag}-email-field`}
                  message={errors.email?.message}
                />
              </div>
            </div>
            <div className="newsletter" style={{ marginBottom: showWarning ? 0 : '28px' }}>
              <div className="checkbox-area">
                <div className="checkbox-confirm">
                  <input
                    {...register('consent')}
                    type="checkbox"
                    id={consentId}
                    aria-describedby={showWarning ? warningId : undefined}
                  />
                  <p className="consent-copy">
                    <label htmlFor={consentId}>{CONSENT_LABEL}</label>{' '}
                    <a
                      href={PRIVACY_POLICY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="warning" aria-live="polite">
              {showWarning && (
                <p className="danger" id={warningId}>
                  {CONSENT_WARNING}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="btn -wide -blue-2"
              disabled={!dirtyFields.email || formValues.isCTADisabled || !consent}
              aria-label="Enter your email and click submit to receive your free content"
            >
              Get Free Access
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

LeadMagnetFunnelForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  tag: PropTypes.string.isRequired,
  isPopUp: PropTypes.bool,
  hasSubscription: PropTypes.bool,
  formValues: PropTypes.shape({
    isCTADisabled: PropTypes.bool
  })
}

export default connect(
  stateSelector,
  null,
  dispatchSelector
)(LeadMagnetFunnelForm)
